import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Tag, Plus, RefreshCw, X, FileText,
  Settings2, AlertTriangle, Truck, CheckCircle2, Trash2, ExternalLink,
  Scissors, FileDown, Download, Package, Pencil, Check, Lock,
} from 'lucide-react';
import apiClient from '../../api/apiClient';
import { Sale, StockItem, SALE_STATUS_LABELS, ShowNotificationFn } from './crm.types';
import { CrmEmailSyncBanner } from './CrmEmailSyncBanner';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatPrice(n?: number): string {
  if (n == null) return '—';
  return n.toFixed(2) + ' €';
}

/** Total « Acheté » comme sur la page Stock : prix article + livraison + protection acheteur. */
function stockTotalAcquisition(item: StockItem): number {
  return (item.purchase_price || 0) + (item.shipping_cost || 0) + (item.protection_fee || 0);
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function monthKey(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key: string): string {
  const [y, m] = key.split('-');
  return `${MONTHS_FR[parseInt(m, 10) - 1]} ${y}`;
}

const IN_PROGRESS_STATUSES = ['sold', 'packed', 'shipped', 'delivered'] as const;

function formatDeadline(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function isShippingUrgent(deadline?: string): boolean {
  if (!deadline) return false;
  return new Date(deadline).getTime() - Date.now() < 2 * 86400 * 1000;
}

function calcNet(price: number, tvaRate: number, packagingCost: number): number {
  if (tvaRate === 0 && packagingCost === 0) return price;
  return price - price * (tvaRate / 100) - packagingCost;
}

/** Date / heure affichée sous le badge de statut (échéance, expédition, finalisation). */
function saleCardSidebarDatetime(sale: Sale): { line: string; urgent: boolean } | null {
  if (sale.status === 'completed') {
    if (sale.is_manual && sale.completed_at) return { line: formatDeadline(sale.completed_at), urgent: false };
    if (sale.email_received_at) return { line: formatDeadline(sale.email_received_at), urgent: false };
    return null;
  }
  if (sale.status === 'shipped' && sale.shipped_at) {
    return { line: formatDeadline(sale.shipped_at), urgent: false };
  }
  // En cours : afficher la date d’expédition (moment où le colis est passé « expédié »)
  if (sale.status === 'delivered') {
    if (sale.shipped_at) return { line: formatDeadline(sale.shipped_at), urgent: false };
    if (sale.delivered_at) return { line: formatDeadline(sale.delivered_at), urgent: false };
    return null;
  }
  if (sale.shipping_deadline && (sale.status === 'sold' || sale.status === 'packed')) {
    return {
      line: formatDeadline(sale.shipping_deadline),
      urgent: isShippingUrgent(sale.shipping_deadline),
    };
  }
  return null;
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

type TabKey = 'all' | 'to_ship' | 'in_progress' | 'finalized';

const TABS: { key: TabKey; label: string; statuses: string[] | null }[] = [
  { key: 'to_ship',     label: 'À expédier', statuses: ['sold', 'packed'] },
  { key: 'in_progress', label: 'En cours',   statuses: ['shipped', 'delivered'] },
  { key: 'finalized',   label: 'Finalisées', statuses: ['completed'] },
  { key: 'all',         label: 'Toutes',     statuses: null },
];

// ── Modal paramètres TVA / emballage ─────────────────────────────────────────

interface SettingsModalProps {
  tvaRate: number;
  packagingCost: number;
  vatNumber: string;
  onSave: (tva: number, pkg: number, vatNum: string) => void;
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ tvaRate, packagingCost, vatNumber, onSave, onClose }) => {
  const [tva, setTva] = useState(String(tvaRate));
  const [pkg, setPkg] = useState(String(packagingCost));
  const [vat, setVat] = useState(vatNumber || '');

  useEffect(() => {
    setTva(String(tvaRate));
    setPkg(String(packagingCost));
    setVat(vatNumber || '');
  }, [tvaRate, packagingCost, vatNumber]);

  const previewNet = () => {
    const t = parseFloat(tva) || 0;
    const p = parseFloat(pkg) || 0;
    return calcNet(25, t, p).toFixed(2);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">Paramètres de calcul</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Taux TVA (%)</label>
            <input
              type="number" step="0.1" min="0" max="100"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={tva} onChange={e => setTva(e.target.value)} placeholder="0"
            />
            <p className="text-xs text-gray-400 mt-1">Ex : 20 pour 20 %. Mettre 0 si non applicable.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frais d'emballage (€ / vente)</label>
            <input
              type="number" step="0.01" min="0"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={pkg} onChange={e => setPkg(e.target.value)} placeholder="0.00"
            />
            <p className="text-xs text-gray-400 mt-1">Ex : 0.50 € de matériel d'emballage par colis.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">N° TVA (affiché sur les factures PDF ventes finalisées)</label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={vat} onChange={e => setVat(e.target.value)} placeholder="ex. FRxx…"
            />
            <p className="text-xs text-gray-400 mt-1">Entre le n° de transaction et la date sur le PDF. Laisser vide si vous n’en avez pas.</p>
          </div>
          <div className="bg-emerald-50 rounded-xl px-4 py-3">
            <p className="text-xs font-semibold text-emerald-800 mb-1">Aperçu (sur une vente à 25 €)</p>
            <p className="text-sm text-emerald-700">
              25.00 € − TVA − emballage = <span className="font-bold">{previewNet()} €</span>
            </p>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50">
              Annuler
            </button>
            <button
              onClick={() => { onSave(parseFloat(tva) || 0, parseFloat(pkg) || 0, vat.trim()); onClose(); }}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-semibold hover:opacity-90">
 Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Modal ajout vente ─────────────────────────────────────────────────────────

interface AddSaleModalProps {
  onClose: () => void;
  onSaved: () => void;
  initialStatus?: string;
}

const AddSaleModal: React.FC<AddSaleModalProps> = ({ onClose, onSaved, initialStatus = 'sold' }) => {
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [form, setForm] = useState({
    item_title: '', stock_item_id: '',
    sale_price: '', buyer_name: '',
    carrier: '', tracking_number: '', sale_date: '', notes: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiClient.get('/crm/stock?scope=effective').then(r => setStockItems(r.data)).catch(() => {});
  }, []);

  const handleStockSelect = (id: string) => {
    const item = stockItems.find(i => String(i.id) === id);
    setForm(f => ({
      ...f,
      stock_item_id: id,
      item_title: item ? item.item_title : f.item_title,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const dateIso = form.sale_date ? new Date(form.sale_date).toISOString() : undefined;
      const base: Record<string, unknown> = {
        item_title: form.item_title,
        stock_item_id: form.stock_item_id ? parseInt(form.stock_item_id, 10) : undefined,
        sale_price: parseFloat(form.sale_price) || 0,
        shipping_cost: 0,
        vinted_commission: 0,
        buyer_name: form.buyer_name || undefined,
        carrier: form.carrier || undefined,
        tracking_number: form.tracking_number || undefined,
        notes: form.notes || undefined,
        status: initialStatus,
      };
      if (initialStatus === 'sold' || initialStatus === 'packed') {
        base.shipping_deadline = dateIso;
      } else if (initialStatus === 'shipped') {
        base.shipped_at = dateIso;
      } else if (initialStatus === 'completed') {
        base.completed_at = dateIso;
      }
      await apiClient.post('/crm/sales', base);
      onSaved(); onClose();
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">Enregistrer une vente</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {stockItems.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Article du stock (optionnel)</label>
              <select
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                value={form.stock_item_id} onChange={e => handleStockSelect(e.target.value)}>
                <option value="">— Sélectionner —</option>
                {stockItems.map(i => (
                  <option key={i.id} value={i.id}>{i.item_title}{i.item_size ? ` (${i.item_size})` : ''}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre de l'article *</label>
            <input type="text" required
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={form.item_title} onChange={e => setForm(f => ({ ...f, item_title: e.target.value }))}
              placeholder="Ex : Robe H&M taille S" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix de vente *</label>
              <input type="number" step="0.01" required
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                value={form.sale_price} onChange={e => setForm(f => ({ ...f, sale_price: e.target.value }))}
                placeholder="25.00" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Acheteur</label>
              <input type="text"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                value={form.buyer_name} onChange={e => setForm(f => ({ ...f, buyer_name: e.target.value }))}
                placeholder="Pseudo Vinted" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input type="date"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={form.sale_date} onChange={e => setForm(f => ({ ...f, sale_date: e.target.value }))} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50">
              Annuler
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Modal fusion PDF bordereaux ───────────────────────────────────────────────

interface BulkLabelModalProps {
  count: number;
  onClose: () => void;
  onChoose: (mode: 'full' | 'cropped') => void;
  progress: { current: number; total: number } | null;
  readyUrl: { url: string; filename: string } | null;
}

const BulkLabelModal: React.FC<BulkLabelModalProps> = ({ count, onClose, onChoose, progress, readyUrl }) => {
  const isLoading = progress !== null;
  const pct = isLoading ? Math.round((progress!.current / progress!.total) * 100) : 0;

  const handleDownload = () => {
    if (!readyUrl) return;
    const a = document.createElement('a');
    a.href = readyUrl.url;
    a.download = readyUrl.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleOpenTab = () => {
    if (!readyUrl) return;
    window.open(readyUrl.url, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-slide-up">

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-emerald-600" />
            <h3 className="text-base font-bold text-gray-900">
              {readyUrl ? 'PDF prêt !' : 'Ouvrir les bordereaux'}
            </h3>
          </div>
          {!isLoading && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          )}
        </div>

        <div className="px-5 py-4">
          {isLoading ? (
            /* ── Barre de progression ── */
            <div className="py-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Préparation du PDF...</span>
                <span className="text-sm font-semibold text-emerald-700">{progress!.current}/{progress!.total}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                <div
                  className="h-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2 text-center">
                Fusion de {progress!.total} bordereau{progress!.total > 1 ? 'x' : ''} en cours...
              </p>
            </div>
          ) : readyUrl ? (
            /* ── PDF prêt : choix télécharger ou ouvrir ── */
            <div className="py-2">
              <p className="text-sm text-gray-500 mb-4 text-center">
                Que souhaitez-vous faire avec le PDF ?
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleDownload}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all group">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                    <FileDown size={20} className="text-gray-500 group-hover:text-emerald-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-800">Télécharger</p>
                    <p className="text-xs text-gray-400 mt-0.5">Enregistrer le fichier</p>
                  </div>
                </button>
                <button
                  onClick={handleOpenTab}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all group">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                    <ExternalLink size={20} className="text-gray-500 group-hover:text-emerald-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-800">Ouvrir</p>
                    <p className="text-xs text-gray-400 mt-0.5">Nouvel onglet</p>
                  </div>
                </button>
              </div>
            </div>
          ) : (
            /* ── Choix du mode ── */
            <>
              <p className="text-sm text-gray-500 mb-4">
                <span className="font-semibold text-gray-800">{count}</span> bordereau{count > 1 ? 'x' : ''} sélectionné{count > 1 ? 's' : ''} — choisissez le format :
              </p>
              <div className="grid grid-cols-2 gap-3">

                {/* Complet */}
                <button
                  onClick={() => onChoose('full')}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all group">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                    <FileDown size={20} className="text-gray-500 group-hover:text-emerald-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-800">Complet</p>
                    <p className="text-xs text-gray-400 mt-0.5">Page entière</p>
                  </div>
                </button>

                {/* Rogné */}
                <button
                  onClick={() => onChoose('cropped')}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all group">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                    <Scissors size={20} className="text-gray-500 group-hover:text-emerald-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-800">Rogné</p>
                    <p className="text-xs text-gray-400 mt-0.5">Label seul, portrait</p>
                  </div>
                </button>

              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Modal factures ventes (même logique que ReceiptsModal achats + option « en cours ») ──

interface SalesInvoiceModalProps {
  sales: Sale[];
  preSelected: Set<number>;
  onClose: () => void;
  showNotification: ShowNotificationFn;
}

function saleInInvoicePool(s: Sale, includeInProgress: boolean): boolean {
  if (s.status === 'completed') return true;
  if (!includeInProgress) return false;
  return (IN_PROGRESS_STATUSES as readonly string[]).includes(s.status);
}

function initialInvoiceMonths(
  allSales: Sale[],
  preSelected: Set<number>,
  includeInProgress: boolean,
): Set<string> {
  const pool = allSales.filter(s => saleInInvoicePool(s, includeInProgress));
  const keys = Array.from(new Set(pool.map(s => monthKey(s.created_at)).filter(Boolean))).sort(
    (a, b) => b.localeCompare(a),
  );
  if (keys.length === 0) return new Set();
  if (preSelected.size === 0) return new Set([keys[0]]);
  const fromPre = Array.from(
    new Set(pool.filter(s => preSelected.has(s.id)).map(s => monthKey(s.created_at)).filter(Boolean)),
  ).sort((a, b) => b.localeCompare(a));
  return new Set(fromPre.length ? fromPre : [keys[0]]);
}

function initialInvoiceSelectedIds(
  allSales: Sale[],
  preSelected: Set<number>,
  months: Set<string>,
  includeInProgress: boolean,
): Set<number> {
  const pool = allSales.filter(s => saleInInvoicePool(s, includeInProgress));
  if (preSelected.size > 0) {
    return new Set(Array.from(preSelected).filter(id => pool.some(s => s.id === id)));
  }
  return new Set(pool.filter(s => months.has(monthKey(s.created_at))).map(s => s.id));
}

const SalesInvoiceModal: React.FC<SalesInvoiceModalProps> = ({
  sales,
  preSelected,
  onClose,
  showNotification,
}) => {
  const hasPreSelected = preSelected.size > 0;
  const [includeInProgress, setIncludeInProgress] = useState(false);

  const tabSales = useMemo(
    () => sales.filter(s => saleInInvoicePool(s, includeInProgress)),
    [sales, includeInProgress],
  );

  const availableMonths = useMemo(() => {
    const keys = Array.from(new Set(tabSales.map(s => monthKey(s.created_at)).filter(Boolean)));
    return keys.sort((a, b) => b.localeCompare(a));
  }, [tabSales]);

  const [selectedMonths, setSelectedMonths] = useState<Set<string>>(() =>
    initialInvoiceMonths(sales, preSelected, false),
  );

  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => {
    const m0 = initialInvoiceMonths(sales, preSelected, false);
    return initialInvoiceSelectedIds(sales, preSelected, m0, false);
  });

  const displayedSales = useMemo(
    () => tabSales.filter(s => selectedMonths.has(monthKey(s.created_at))),
    [tabSales, selectedMonths],
  );

  const [loading, setLoading] = useState(false);

  const setIncludeInProgressAndResync = (checked: boolean) => {
    setIncludeInProgress(checked);
    const ts = sales.filter(s => saleInInvoicePool(s, checked));
    const keys = Array.from(new Set(ts.map(s => monthKey(s.created_at)).filter(Boolean))).sort(
      (a, b) => b.localeCompare(a),
    );
    setSelectedMonths(prev => {
      const kept = Array.from(prev).filter(k => keys.includes(k));
      const nextMonths = new Set(kept.length ? kept : keys.length ? [keys[0]] : []);
      setSelectedIds(new Set(ts.filter(s => nextMonths.has(monthKey(s.created_at))).map(s => s.id)));
      return nextMonths;
    });
  };

  const toggleMonth = (mk: string) => {
    const isSelected = selectedMonths.has(mk);
    if (isSelected && selectedMonths.size === 1) return;
    const nextMonths = new Set(selectedMonths);
    if (isSelected) {
      nextMonths.delete(mk);
      const toRemove = new Set(tabSales.filter(s => monthKey(s.created_at) === mk).map(s => s.id));
      setSelectedIds(prev => new Set(Array.from(prev).filter(id => !toRemove.has(id))));
    } else {
      nextMonths.add(mk);
      const toAdd = tabSales.filter(s => monthKey(s.created_at) === mk).map(s => s.id);
      setSelectedIds(prev => new Set(Array.from(prev).concat(toAdd)));
    }
    setSelectedMonths(nextMonths);
  };

  const toggleId = (id: number) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => {
    if (selectedIds.size === displayedSales.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayedSales.map(s => s.id)));
    }
  };

  const handleGenerate = async (mode: 'open' | 'download') => {
    if (selectedIds.size === 0) {
      showNotification('Aucune vente sélectionnée', 'Coche au moins une vente dans la liste.', 'error');
      return;
    }
    setLoading(true);
    try {
      const blob = await apiClient.postBlob('/crm/sales/invoices/pdf', {
        sale_ids: Array.from(selectedIds),
      });
      const url = URL.createObjectURL(blob);
      if (mode === 'open') {
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `factures_ventes_${Array.from(selectedMonths).sort().join('_') || 'export'}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
      showNotification('PDF généré', mode === 'open' ? 'Ouverture dans un nouvel onglet.' : 'Téléchargement lancé.', 'success');
    } catch {
      showNotification('Erreur', 'Impossible de générer le PDF des factures.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">

        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-emerald-600" />
            <h3 className="text-base font-bold text-gray-900">Éditer les factures</h3>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 pt-4 pb-2 flex-shrink-0 space-y-3">
          {hasPreSelected && (
            <p className="text-xs text-gray-500 leading-snug">
              {preSelected.size} vente{preSelected.size > 1 ? 's' : ''} cochée{preSelected.size > 1 ? 's' : ''} dans la liste — tu peux quand même filtrer par mois et inclure les ventes en cours.
            </p>
          )}
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeInProgress}
              onChange={e => setIncludeInProgressAndResync(e.target.checked)}
              className="mt-0.5 rounded border-gray-300 accent-emerald-600"
            />
            <span className="text-xs text-gray-600 leading-snug">
              Inclure les ventes <strong>en cours</strong> (à confirmer, à expédier, expédiées, livrées) en plus des finalisées, pour les choisir par mois comme les autres.
            </span>
          </label>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Choisir un ou plusieurs mois
            </p>
            {availableMonths.length === 0 ? (
              <p className="text-sm text-gray-400 italic">Aucune vente dans ce périmètre</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableMonths.map(mk => (
                  <button
                    key={mk}
                    type="button"
                    onClick={() => toggleMonth(mk)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      selectedMonths.has(mk)
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {monthLabel(mk)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {displayedSales.length > 0 && (
          <>
            <div className="px-6 py-2 flex items-center justify-between flex-shrink-0 border-t border-gray-50">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={selectedIds.size === displayedSales.length && displayedSales.length > 0}
                  onChange={toggleAll}
                  className="w-4 h-4 rounded border-gray-300 accent-emerald-600 cursor-pointer"
                />
                <span className="text-xs text-gray-500 font-medium">
                  {selectedIds.size > 0
                    ? `${selectedIds.size} sélectionné${selectedIds.size > 1 ? 's' : ''}`
                    : 'Tout sélectionner'}
                </span>
              </label>
              <span className="text-xs text-gray-400">
                {displayedSales.length} vente{displayedSales.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="overflow-y-auto px-6 pb-2 flex-1 space-y-1.5">
              {displayedSales.map(s => {
                const st = SALE_STATUS_LABELS[s.status];
                return (
                  <label
                    key={s.id}
                    className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                      selectedIds.has(s.id)
                        ? 'bg-emerald-50 border border-emerald-200'
                        : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(s.id)}
                      onChange={() => toggleId(s.id)}
                      className="w-4 h-4 rounded border-gray-300 accent-emerald-600 cursor-pointer flex-shrink-0"
                    />
                    {s.item_image_url ? (
                      <img src={s.item_image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Tag size={16} className="text-emerald-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{s.item_title}</p>
                      <p className="text-xs text-gray-400">{formatDate(s.created_at)}</p>
                      {s.status !== 'completed' && (
                        <span className={`inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-md ${st.bg} ${st.color}`}>
                          {st.label}
                        </span>
                      )}
                    </div>
                    <span className="text-sm font-bold text-emerald-700 flex-shrink-0">{formatPrice(s.sale_price)}</span>
                  </label>
                );
              })}
            </div>
          </>
        )}

        {displayedSales.length === 0 && (
          <div className="px-6 py-8 text-center text-gray-400 flex-1">
            <Tag size={36} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Aucune vente pour la sélection</p>
          </div>
        )}

        <div className="flex items-center gap-2 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => { void handleGenerate('open'); }}
            disabled={loading || selectedIds.size === 0}
            className="flex items-center justify-center gap-1.5 flex-1 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100 disabled:opacity-40 transition-colors"
          >
            <ExternalLink size={14} /> Ouvrir
          </button>
          <button
            type="button"
            onClick={() => { void handleGenerate('download'); }}
            disabled={loading || selectedIds.size === 0}
            className="flex items-center justify-center gap-1.5 flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition-all shadow-sm"
          >
            {loading ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
            {loading ? 'Génération…' : 'Télécharger'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal bordereau individuel ────────────────────────────────────────────────

interface SingleLabelModalProps {
  sale: Sale;
  onClose: () => void;
  showNotification: ShowNotificationFn;
}

const SingleLabelModal: React.FC<SingleLabelModalProps> = ({ sale, onClose, showNotification }) => {
  const [step, setStep] = useState<'choose' | 'loading' | 'ready'>('choose');
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState('');

  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  const handleChoose = async (mode: 'full' | 'cropped') => {
    setStep('loading');
    try {
      let blob: Blob;
      if (mode === 'full') {
        blob = await apiClient.getBlob(`/crm/sales/${sale.id}/label`);
      } else {
        blob = await apiClient.postBlob('/crm/sales/labels/merge', { sale_ids: [sale.id], mode: 'cropped' });
      }
      const url = URL.createObjectURL(blob);
      const base = (sale.shipping_label_filename || 'bordereau').replace(/\.pdf$/i, '');
      setBlobUrl(url);
      setFilename(`${base}_${mode === 'full' ? 'complet' : 'rogne'}.pdf`);
      setStep('ready');
    } catch {
      showNotification('Erreur', 'Impossible de charger le bordereau', 'error');
      setStep('choose');
    }
  };

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleOpen = () => {
    if (!blobUrl) return;
    window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl!), 60_000);
    setBlobUrl(null);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[300] flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Package size={18} className="text-emerald-600" />
            <h3 className="text-base font-bold text-gray-900">
              {step === 'ready' ? 'Bordereau prêt !' : step === 'loading' ? 'Chargement…' : 'Ouvrir le bordereau'}
            </h3>
          </div>
          {step !== 'loading' && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          )}
        </div>
        <div className="px-5 py-4">
          {step === 'loading' ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw size={24} className="animate-spin text-emerald-400" />
            </div>
          ) : step === 'ready' ? (
            <div className="py-2">
              <p className="text-sm text-gray-500 mb-4 text-center">Que souhaitez-vous faire avec le bordereau ?</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleDownload}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all group">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                    <FileDown size={20} className="text-gray-500 group-hover:text-emerald-600" />
                  </div>
                  <p className="text-sm font-semibold text-gray-800">Télécharger</p>
                </button>
                <button
                  onClick={handleOpen}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all group">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                    <ExternalLink size={20} className="text-gray-500 group-hover:text-emerald-600" />
                  </div>
                  <p className="text-sm font-semibold text-gray-800">Ouvrir</p>
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4 truncate">
                <span className="font-semibold text-gray-800">{sale.item_title}</span> — choisissez le format :
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleChoose('full')}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all group">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                    <FileDown size={20} className="text-gray-500 group-hover:text-emerald-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-800">Complet</p>
                    <p className="text-xs text-gray-400 mt-0.5">Page entière</p>
                  </div>
                </button>
                <button
                  onClick={() => handleChoose('cropped')}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all group">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-emerald-100 flex items-center justify-center transition-colors">
                    <Scissors size={20} className="text-gray-500 group-hover:text-emerald-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-800">Rogné</p>
                    <p className="text-xs text-gray-400 mt-0.5">Label seul, portrait</p>
                  </div>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Dropdown custom Prix achat ────────────────────────────────────────────────

interface StockDropdownProps {
  stockItems: StockItem[];
  value: string;           // '' = manuel, sinon id
  onChange: (val: string) => void;
  disabled?: boolean;
}

const StockDropdown: React.FC<StockDropdownProps> = ({ stockItems, value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = stockItems.find(i => String(i.id) === value);
  const label = selected ? selected.item_title : 'Manuel';

  // Fermer si clic extérieur
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (stockItems.length === 0) return null;

  if (disabled) {
    return (
      <div className="w-full flex items-center gap-1 bg-gray-100 border border-gray-100 rounded-lg px-2 py-1 text-[10px] font-medium text-gray-600">
        <span className="truncate max-w-[80px]">{label}</span>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1 text-[10px] font-medium text-gray-700 hover:border-emerald-400 transition-colors focus:outline-none focus:ring-1 focus:ring-emerald-400">
        <span className="truncate max-w-[80px]">{label}</span>
        <svg className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 w-48 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden">
          {/* Manuel */}
          <button
            type="button"
            onClick={() => { onChange(''); setOpen(false); }}
            className={`w-full text-left px-3 py-2 text-xs transition-colors ${!value ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}>
            Manuel
          </button>
          {/* Articles stock */}
          {stockItems.map(item => (
            <button
              key={item.id}
              type="button"
              onClick={() => { onChange(String(item.id)); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs transition-colors border-t border-gray-50 ${String(item.id) === value ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}>
              <p className="truncate font-medium">{item.item_title}</p>
              {stockTotalAcquisition(item) > 0 && (
                <p className="text-[10px] text-gray-400 mt-0.5">Acheté {stockTotalAcquisition(item).toFixed(2)} €</p>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Carte vente ───────────────────────────────────────────────────────────────

interface SaleCardProps {
  sale: Sale;
  listTab: TabKey;
  defaultTvaRate: number;
  defaultPackagingCost: number;
  stockItems: StockItem[];
  selected: boolean;
  syncEnabled: boolean;
  showNotification: ShowNotificationFn;
  onToggleSelect: (id: number) => void;
  onStatusChange: (id: number, status: string, purchaseMeta?: {
    stock_item_id: number | null;
    purchase_price: number;
    crm_tva_rate: number;
    crm_packaging_cost: number;
  }) => void;
  onPatchPurchaseLink: (id: number, body: { stock_item_id: number | null; purchase_price: number }) => Promise<void>;
  onPatchSalePrice: (id: number, sale_price: number) => Promise<void>;
  onPatchCrmFiscal: (id: number, body: { crm_tva_rate: number; crm_packaging_cost: number }) => Promise<void>;
}

const SaleCard: React.FC<SaleCardProps> = ({
  sale, listTab, defaultTvaRate, defaultPackagingCost, stockItems,
  selected, syncEnabled, showNotification, onToggleSelect, onStatusChange,
  onPatchPurchaseLink, onPatchSalePrice, onPatchCrmFiscal,
}) => {
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [financialsUnlocked, setFinancialsUnlocked] = useState(false);
  const isToShipSale = sale.status === 'sold' || sale.status === 'packed';
  const useModifierFlow =
    listTab === 'in_progress'
    || listTab === 'finalized'
    || (listTab === 'all' && !isToShipSale);
  const allowEditFinancials =
    ((listTab === 'to_ship' || (listTab === 'all' && isToShipSale)) && isToShipSale)
    || (useModifierFlow && financialsUnlocked);
  const deferSaveUntilValidate = useModifierFlow && financialsUnlocked;
  const showBordereauButton = !!sale.shipping_label_filename
    && !sale.from_sold_email
    && (listTab === 'to_ship' || (listTab === 'all' && isToShipSale));
  const [localTva, setLocalTva] = useState(defaultTvaRate);
  const [localPkg, setLocalPkg] = useState(defaultPackagingCost);
  const [stockSelection, setStockSelection] = useState('');
  const [manualPurchase, setManualPurchase] = useState('');
  const [salePriceInput, setSalePriceInput] = useState('');

  useEffect(() => {
    setFinancialsUnlocked(false);
  }, [sale.id, listTab]);

  useEffect(() => {
    setLocalTva(sale.crm_tva_rate ?? defaultTvaRate);
    setLocalPkg(sale.crm_packaging_cost ?? defaultPackagingCost);
  }, [sale.id, sale.crm_tva_rate, sale.crm_packaging_cost, defaultTvaRate, defaultPackagingCost]);

  useEffect(() => {
    if (sale.stock_item_id) {
      setStockSelection(String(sale.stock_item_id));
      setManualPurchase('');
    } else {
      setStockSelection('');
      setManualPurchase(sale.purchase_price != null ? String(sale.purchase_price) : '');
    }
  }, [sale.id, sale.stock_item_id, sale.purchase_price]);

  useEffect(() => {
    setSalePriceInput(sale.sale_price != null ? String(sale.sale_price) : '');
  }, [sale.id, sale.sale_price]);

  const syncPurchaseToServer = async (sel: string, manualStr: string) => {
    if (!allowEditFinancials || deferSaveUntilValidate) return;
    const stock_item_id = sel ? parseInt(sel, 10) : null;
    const stockItemRow = sel ? stockItems.find(i => String(i.id) === sel) : undefined;
    const purchase_price =
      sel && stockItemRow ? stockTotalAcquisition(stockItemRow) : (parseFloat(manualStr) || 0);
    const prevSid = sale.stock_item_id ?? null;
    const prevPrice = sale.purchase_price ?? 0;
    if (prevSid === stock_item_id && Math.abs(prevPrice - purchase_price) < 0.001) return;
    await onPatchPurchaseLink(sale.id, { stock_item_id, purchase_price });
  };

  const buildPurchaseMeta = () => {
    const sidStr = stockSelection || (sale.stock_item_id != null ? String(sale.stock_item_id) : '');
    const stock_item_id = sidStr ? parseInt(sidStr, 10) : null;
    const it = stock_item_id != null
      ? stockItems.find(i => i.id === stock_item_id)
      : undefined;
    let purchase_price: number;
    if (stock_item_id != null && it) {
      purchase_price = stockTotalAcquisition(it);
    } else if (stock_item_id != null && sale.purchase_price != null) {
      purchase_price = sale.purchase_price;
    } else if (!stock_item_id) {
      purchase_price = parseFloat(manualPurchase) || 0;
    } else {
      purchase_price = sale.purchase_price ?? 0;
    }
    return {
      stock_item_id,
      purchase_price,
      crm_tva_rate: localTva,
      crm_packaging_cost: localPkg,
    };
  };

  const hasLabel = !!sale.shipping_label_filename;

  const validateFinancialEdits = async () => {
    if (!useModifierFlow || !financialsUnlocked) return;
    try {
      const meta = buildPurchaseMeta();
      await onPatchPurchaseLink(sale.id, {
        stock_item_id: meta.stock_item_id,
        purchase_price: meta.purchase_price,
      });
      const sp = parseFloat(salePriceInput);
      if (Number.isFinite(sp)) {
        await onPatchSalePrice(sale.id, sp);
      }
      await onPatchCrmFiscal(sale.id, {
        crm_tva_rate: localTva,
        crm_packaging_cost: localPkg,
      });
      setFinancialsUnlocked(false);
      showNotification('Enregistré', 'Montants mis à jour.', 'success');
    } catch {
      showNotification('Erreur', 'Enregistrement impossible.', 'error');
    }
  };

  const cancelFinancialEdits = () => {
    setFinancialsUnlocked(false);
    setLocalTva(sale.crm_tva_rate ?? defaultTvaRate);
    setLocalPkg(sale.crm_packaging_cost ?? defaultPackagingCost);
    if (sale.stock_item_id) {
      setStockSelection(String(sale.stock_item_id));
      setManualPurchase('');
    } else {
      setStockSelection('');
      setManualPurchase(sale.purchase_price != null ? String(sale.purchase_price) : '');
    }
    setSalePriceInput(sale.sale_price != null ? String(sale.sale_price) : '');
  };

  // Badge de statut :
  // - from_sold_email=true + pas de bordereau → rouge "À valider sur Vinted"
  // - sinon → labels standards
  const isAValider = sale.from_sold_email && !sale.shipping_label_filename;
  const statusInfo = (() => {
    if (isAValider) {
      return { label: 'À valider sur Vinted', color: 'text-red-700', bg: 'bg-red-50' };
    }
    return SALE_STATUS_LABELS[sale.status] || { label: sale.status, color: 'text-gray-600', bg: 'bg-gray-100' };
  })();
  const urgent = isShippingUrgent(sale.shipping_deadline) && (sale.status === 'sold' || sale.status === 'packed');

  const resolvedPurchaseEditable = stockSelection
    ? (() => {
        const it = stockItems.find(i => String(i.id) === stockSelection);
        return it ? stockTotalAcquisition(it) : (sale.purchase_price ?? 0);
      })()
    : (parseFloat(manualPurchase) || 0);

  // Hors « À expédier » : afficher le coût enregistré sur la vente uniquement (figé après expédition, etc.)
  const resolvedPurchaseReadonly =
    sale.purchase_price != null
      ? sale.purchase_price
      : sale.stock_item_id
        ? (() => {
            const it = stockItems.find(i => i.id === sale.stock_item_id);
            return it ? stockTotalAcquisition(it) : 0;
          })()
        : 0;

  const resolvedPurchase = allowEditFinancials ? resolvedPurchaseEditable : resolvedPurchaseReadonly;

  const displayTva = allowEditFinancials ? localTva : (sale.crm_tva_rate ?? defaultTvaRate);
  const displayPkg = allowEditFinancials ? localPkg : (sale.crm_packaging_cost ?? defaultPackagingCost);

  const commitCrmFiscal = () => {
    if (!allowEditFinancials || deferSaveUntilValidate) return;
    const prevT = sale.crm_tva_rate ?? defaultTvaRate;
    const prevP = sale.crm_packaging_cost ?? defaultPackagingCost;
    if (Math.abs(localTva - prevT) < 0.0001 && Math.abs(localPkg - prevP) < 0.001) return;
    void onPatchCrmFiscal(sale.id, { crm_tva_rate: localTva, crm_packaging_cost: localPkg });
  };

  const salePriceNum = (() => {
    if (allowEditFinancials) {
      const p = parseFloat(salePriceInput);
      return Number.isFinite(p) ? p : (sale.sale_price ?? 0);
    }
    return sale.sale_price ?? 0;
  })();

  const tvaAmount = salePriceNum * displayTva / 100;
  const computedNet = salePriceNum - resolvedPurchase - tvaAmount - displayPkg;

  const commitSalePrice = () => {
    if (!allowEditFinancials || deferSaveUntilValidate) return;
    const v = parseFloat(salePriceInput);
    if (!Number.isFinite(v)) return;
    if (Math.abs(v - (sale.sale_price ?? 0)) < 0.001) return;
    void onPatchSalePrice(sale.id, v);
  };

  const readonlyStockLabel = sale.stock_item_id
    ? (stockItems.find(i => i.id === sale.stock_item_id)?.item_title ?? 'Stock')
    : 'Manuel';

  return (
    <div className={`bg-white border rounded-xl shadow-sm transition-all ${
      selected ? 'border-emerald-400 ring-1 ring-emerald-200' :
      urgent ? 'border-amber-300 ring-1 ring-amber-200' : 'border-gray-100'
    }`}>

      <div className="flex items-start gap-2 px-3 pt-2.5 pb-2">

        <input type="checkbox" checked={selected} onChange={() => onToggleSelect(sale.id)}
          className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-emerald-600 cursor-pointer flex-shrink-0" />

        <div className="w-11 h-11 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {sale.item_image_url
            ? <img src={sale.item_image_url} alt={sale.item_title} className="w-full h-full object-cover rounded-lg" />
            : <Tag size={18} className="text-emerald-500" />}
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-[13px] leading-tight truncate">{sale.item_title}</p>
          {sale.buyer_name && (
            <p className="text-xs text-gray-500 mt-0 truncate">👤 {sale.buyer_name}</p>
          )}
          <span className="font-bold text-emerald-700 text-xs mt-0.5 block">{formatPrice(salePriceNum)}</span>
        </div>

        <div className="flex flex-col items-end flex-shrink-0 gap-0.5 ml-1 max-w-[46%]">
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${statusInfo.bg} ${statusInfo.color}`}>
            {statusInfo.label}
          </span>
          {(() => {
            const ev = saleCardSidebarDatetime(sale);
            if (!ev) return null;
            const urg = ev.urgent;
            return (
              <span className={`text-[10px] text-right leading-tight ${urg ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>
                {urg ? '⚡ ' : '📅 '}{ev.line}
              </span>
            );
          })()}
          {showBordereauButton && (
            <button
              type="button"
              onClick={() => setShowLabelModal(true)}
              className="flex items-center gap-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-md px-1.5 py-0.5 text-[10px] font-semibold transition-colors mt-0.5"
              title="Ouvrir le bordereau d'expédition"
            >
              <Package size={10} />
              Bordereau
            </button>
          )}
          {useModifierFlow && !financialsUnlocked && (
            <button
              type="button"
              onClick={() => setFinancialsUnlocked(true)}
              className="flex items-center gap-1 bg-violet-100 hover:bg-violet-200 text-violet-800 rounded-md px-1.5 py-0.5 text-[10px] font-semibold transition-colors mt-0.5"
              title="Modifier prix, achat, TVA et emballage"
            >
              <Pencil size={10} />
              Modifier
            </button>
          )}
          {useModifierFlow && financialsUnlocked && (
            <div className="flex flex-col gap-1 items-end mt-0.5">
              <button
                type="button"
                onClick={() => void validateFinancialEdits()}
                className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-2 py-0.5 text-[10px] font-semibold shadow-sm"
              >
                <Check size={10} />
                Valider
              </button>
              <button
                type="button"
                onClick={cancelFinancialEdits}
                className="text-[10px] text-gray-500 hover:text-gray-700 font-medium underline"
              >
                Annuler
              </button>
            </div>
          )}
        </div>
      </div>

      {sale.status === 'sold' && !hasLabel && !sale.is_manual && (
        <div className="mx-3 mb-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 flex items-start gap-1.5">
          <AlertTriangle size={11} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-[10px] text-amber-800 leading-snug">
            <span className="font-semibold">Action requise :</span> confirme sur Vinted pour recevoir le bordereau.
          </p>
        </div>
      )}

      <div className="border-t border-gray-100 px-3 py-1.5">
        <div className="flex items-stretch gap-1 w-full min-h-0">

          <div className="flex flex-col items-center justify-center bg-gray-50 rounded-lg px-1.5 py-1 flex-1 min-w-0">
            <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wide">Prix vente</span>
            {allowEditFinancials ? (
              <input
                type="number"
                step="0.01"
                min="0"
                value={salePriceInput}
                onChange={e => setSalePriceInput(e.target.value)}
                onBlur={() => { if (!deferSaveUntilValidate) commitSalePrice(); }}
                className="w-full mt-0.5 text-xs font-semibold text-center border border-gray-200 rounded-md px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
            ) : (
              <span className="text-xs font-semibold text-gray-800 mt-0.5">{formatPrice(sale.sale_price)}</span>
            )}
          </div>

          <div className="flex items-center text-gray-300 text-xs self-center px-px">−</div>

          <div className="flex flex-col justify-center bg-gray-50 rounded-lg px-1.5 py-1 flex-[1.35] min-w-0">
            <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wide mb-0.5">Prix achat</span>
            <div className="flex items-center gap-1">
              {stockItems.length > 0 ? (
                <div className="flex-1 min-w-0">
                  <StockDropdown
                    stockItems={stockItems}
                    value={allowEditFinancials ? stockSelection : (sale.stock_item_id ? String(sale.stock_item_id) : '')}
                    disabled={!allowEditFinancials}
                    onChange={val => {
                      if (!allowEditFinancials) return;
                      if (val) {
                        setStockSelection(val);
                        setManualPurchase('');
                        if (!deferSaveUntilValidate) void syncPurchaseToServer(val, '');
                      } else {
                        setStockSelection('');
                        const man = sale.purchase_price != null ? String(sale.purchase_price) : '';
                        setManualPurchase(man);
                        if (!deferSaveUntilValidate) void syncPurchaseToServer('', man);
                      }
                    }}
                  />
                </div>
              ) : !allowEditFinancials ? (
                <span className="text-[10px] text-gray-500 truncate flex-1" title={readonlyStockLabel}>{readonlyStockLabel}</span>
              ) : null}
              {allowEditFinancials && (
                !stockSelection ? (
                  <input type="number" step="0.01" min="0" value={manualPurchase}
                    onChange={e => setManualPurchase(e.target.value)}
                    onBlur={() => {
                      if (!deferSaveUntilValidate) void syncPurchaseToServer(stockSelection, manualPurchase);
                    }}
                    placeholder="0"
                    className="flex-1 text-[10px] border border-gray-200 rounded-md px-1 py-0.5 text-center focus:outline-none focus:ring-1 focus:ring-emerald-400 min-w-0" />
                ) : (
                  <span className="text-xs font-semibold text-emerald-700 flex-shrink-0">{formatPrice(resolvedPurchase)}</span>
                )
              )}
              {!allowEditFinancials && (
                <span className="text-xs font-semibold text-emerald-700 flex-shrink-0">{formatPrice(resolvedPurchase)}</span>
              )}
            </div>
          </div>

          <div className="flex items-center text-gray-300 text-xs self-center px-px">−</div>

          <div className="flex flex-col items-center justify-center bg-gray-50 rounded-lg px-1.5 py-1 flex-1 min-w-0">
            <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wide">TVA</span>
            {allowEditFinancials ? (
              <>
                <div className="flex items-center gap-0.5 mt-0.5">
                  <input type="number" step="0.1" min="0" max="100" value={localTva}
                    onChange={e => setLocalTva(parseFloat(e.target.value) || 0)}
                    onBlur={() => { if (!deferSaveUntilValidate) commitCrmFiscal(); }}
                    className="text-[10px] border border-gray-200 rounded-md px-1 py-0.5 w-9 text-center focus:outline-none focus:ring-1 focus:ring-emerald-400" />
                  <span className="text-[10px] text-gray-400">%</span>
                </div>
                <span className="text-[9px] text-red-400 leading-none">−{formatPrice(tvaAmount)}</span>
              </>
            ) : (
              <>
                <span className="text-xs font-semibold text-gray-800 mt-0.5">{displayTva} %</span>
                <span className="text-[9px] text-red-400 leading-none">−{formatPrice(tvaAmount)}</span>
              </>
            )}
          </div>

          <div className="flex items-center text-gray-300 text-xs self-center px-px">−</div>

          <div className="flex flex-col items-center justify-center bg-gray-50 rounded-lg px-1.5 py-1 flex-1 min-w-0">
            <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wide">Emballage</span>
            {allowEditFinancials ? (
              <>
                <div className="flex items-center gap-0.5 mt-0.5">
                  <input type="number" step="0.01" min="0" value={localPkg}
                    onChange={e => setLocalPkg(parseFloat(e.target.value) || 0)}
                    onBlur={commitCrmFiscal}
                    className="text-[10px] border border-gray-200 rounded-md px-1 py-0.5 w-10 text-center focus:outline-none focus:ring-1 focus:ring-emerald-400" />
                  <span className="text-[10px] text-gray-400">€</span>
                </div>
                <span className="text-[9px] text-red-400 leading-none">−{formatPrice(displayPkg)}</span>
              </>
            ) : (
              <>
                <span className="text-xs font-semibold text-gray-800 mt-0.5">{formatPrice(displayPkg)}</span>
                <span className="text-[9px] text-red-400 leading-none">−{formatPrice(displayPkg)}</span>
              </>
            )}
          </div>

          <div className="flex items-center text-gray-400 text-xs self-center px-px">=</div>

          <div className={`flex flex-col items-center justify-center rounded-lg px-1.5 py-1 flex-1 min-w-0 ${computedNet >= 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
            <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wide">Revenu net</span>
            <span className={`text-xs font-bold mt-0.5 ${computedNet >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
              {formatPrice(computedNet)}
            </span>
          </div>

        </div>
      </div>

      {allowEditFinancials && sale.status === 'packed' && (
        <div className="px-3 pb-2">
          <button
            type="button"
            onClick={() => onStatusChange(sale.id, 'shipped', buildPurchaseMeta())}
            className="w-full py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 flex items-center justify-center gap-1.5">
            <Truck size={12} /> J&apos;ai expédié le colis
          </button>
        </div>
      )}

      {showLabelModal && (
        <SingleLabelModal
          sale={sale}
          onClose={() => setShowLabelModal(false)}
          showNotification={showNotification}
        />
      )}
    </div>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────

interface LabelConflictSale {
  id: number;
  item_title: string;
  buyer_name?: string;
  sale_price?: number;
  tracking_number?: string;
  carrier?: string;
  shipping_label_filename?: string;
  from_sold_email?: boolean;
  created_at?: string;
}

interface LabelConflict {
  id: number;
  conflict_type: 'replace_label' | 'choose_sold_email';
  item_title: string;
  created_at?: string;
  trigger_sale: LabelConflictSale;
  candidates: LabelConflictSale[];
}

interface CrmVentesProps {
  showNotification: ShowNotificationFn;
  onConflictCountChange?: () => void;
  crm_email_active?: boolean;
  is_free_plan?: boolean;
  onUpgrade?: () => void;
}

const CrmVentes: React.FC<CrmVentesProps> = ({ showNotification, onConflictCountChange, crm_email_active, is_free_plan, onUpgrade }) => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>('to_ship');
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [showSyncInfo, setShowSyncInfo] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [tvaRate, setTvaRate] = useState(0);
  const [packagingCost, setPackagingCost] = useState(0);
  const [vatNumber, setVatNumber] = useState('');
  const [showBulkLabelModal, setShowBulkLabelModal] = useState(false);
  const [labelMergeProgress, setLabelMergeProgress] = useState<{ current: number; total: number } | null>(null);
  const [mergedBlobUrl, setMergedBlobUrl] = useState<{ url: string; filename: string } | null>(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [labelConflicts, setLabelConflicts] = useState<LabelConflict[]>([]);

  const loadLabelConflicts = useCallback(async () => {
    try {
      const r = await apiClient.get('/crm/sales/label-conflicts');
      setLabelConflicts(Array.isArray(r.data) ? r.data : []);
    } catch { /* silencieux */ }
  }, []);

  const loadSales = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/crm/sales');
      setSales(res.data);
    } catch {
      showNotification('Erreur', 'Impossible de charger les ventes', 'error');
    } finally { setLoading(false); }
  }, [showNotification]);

  useEffect(() => {
    apiClient.get('/crm/settings').then(r => {
      setTvaRate(r.data.tva_rate ?? 0);
      setPackagingCost(r.data.packaging_cost ?? 0);
      setVatNumber((r.data.vat_number as string) ?? '');
      setSyncEnabled(Boolean(r.data.crm_sales_sync_enabled));
    }).catch(() => {});
    apiClient.get('/crm/stock?scope=effective').then(r => setStockItems(r.data)).catch(() => {});
    loadLabelConflicts();
  }, [loadLabelConflicts]);

  useEffect(() => { loadSales(); }, [loadSales]);

  // Reset sélection quand on change d'onglet
  useEffect(() => { setSelected(new Set()); }, [activeTab]);

  const handleSaveSettings = async (tva: number, pkg: number, vatNum: string) => {
    try {
      await apiClient.patch('/crm/settings', { tva_rate: tva, packaging_cost: pkg, vat_number: vatNum || null });
      setTvaRate(tva);
      setPackagingCost(pkg);
      setVatNumber(vatNum);
      showNotification('Paramètres sauvegardés', '', 'success');
    } catch {
      showNotification('Erreur', 'Impossible de sauvegarder', 'error');
    }
  };

  const patchSalePurchaseLink = async (id: number, body: { stock_item_id: number | null; purchase_price: number }) => {
    try {
      const res = await apiClient.patch(`/crm/sales/${id}`, body);
      setSales(prev => prev.map(s => (s.id === id ? { ...s, ...res.data } : s)));
    } catch {
      showNotification('Erreur', 'Impossible d\'enregistrer le coût d\'achat', 'error');
    }
  };

  const patchSalePrice = async (id: number, sale_price: number) => {
    try {
      const res = await apiClient.patch(`/crm/sales/${id}`, { sale_price });
      setSales(prev => prev.map(s => (s.id === id ? { ...s, ...res.data } : s)));
    } catch {
      showNotification('Erreur', 'Impossible de mettre à jour le prix de vente', 'error');
    }
  };

  const patchSaleCrmFiscal = async (id: number, body: { crm_tva_rate: number; crm_packaging_cost: number }) => {
    try {
      const res = await apiClient.patch(`/crm/sales/${id}`, body);
      setSales(prev => prev.map(s => (s.id === id ? { ...s, ...res.data } : s)));
    } catch {
      showNotification('Erreur', 'Impossible d\'enregistrer la TVA / l\'emballage', 'error');
    }
  };

  const handleStatusChange = async (
    id: number,
    newStatus: string,
    purchaseMeta?: {
      stock_item_id: number | null;
      purchase_price: number;
      crm_tva_rate: number;
      crm_packaging_cost: number;
    },
  ) => {
    try {
      const body: Record<string, unknown> = { status: newStatus };
      if (purchaseMeta) {
        body.stock_item_id = purchaseMeta.stock_item_id;
        body.purchase_price = purchaseMeta.purchase_price;
        body.crm_tva_rate = purchaseMeta.crm_tva_rate;
        body.crm_packaging_cost = purchaseMeta.crm_packaging_cost;
      }
      const res = await apiClient.patch(`/crm/sales/${id}`, body);
      setSales(prev => prev.map(s => (s.id === id ? { ...s, ...res.data } : s)));
      try {
        const st = await apiClient.get('/crm/stock?scope=effective');
        setStockItems(st.data);
      } catch { /* ignore */ }
    } catch {
      showNotification('Erreur', 'Mise à jour échouée', 'error');
    }
  };

  // ── Actions en masse ──────────────────────────────────────────────────────

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(s => s.id)));
    }
  };

  const bulkOpenLabels = () => {
    if (selected.size === 0) {
      showNotification('Aucune sélection', 'Coche une ou plusieurs ventes pour ouvrir leurs bordereaux', 'warning');
      return;
    }
    const withLabel = sales.filter(s => selected.has(s.id) && s.shipping_label_filename);
    if (withLabel.length === 0) {
      showNotification('Aucun bordereau', 'Les ventes sélectionnées n\'ont pas de bordereau disponible', 'warning');
      return;
    }
    setShowBulkLabelModal(true);
  };

  const handleBulkLabelChoose = async (mode: 'full' | 'cropped') => {
    const withLabel = sales.filter(s => selected.has(s.id) && s.shipping_label_filename);
    const saleIds = withLabel.map(s => s.id);
    setLabelMergeProgress({ current: 0, total: saleIds.length });

    try {
      // On simule une progression pendant le traitement serveur
      const tickInterval = setInterval(() => {
        setLabelMergeProgress(prev => {
          if (!prev || prev.current >= prev.total - 1) return prev;
          return { ...prev, current: prev.current + 1 };
        });
      }, 400);

      const blob = await apiClient.postBlob(
        '/crm/sales/labels/merge',
        { sale_ids: saleIds, mode }
      );

      clearInterval(tickInterval);
      setLabelMergeProgress({ current: saleIds.length, total: saleIds.length });

      await new Promise(r => setTimeout(r, 400)); // laisser la barre à 100% 400ms

      const url = URL.createObjectURL(blob);
      const filename = `bordereaux_${mode === 'cropped' ? 'rognes' : 'complets'}.pdf`;
      setMergedBlobUrl({ url, filename });

    } catch {
      showNotification('Erreur', 'Impossible de générer le PDF', 'error');
      setShowBulkLabelModal(false);
    } finally {
      setLabelMergeProgress(null);
    }
  };

  const purchaseMetaForShip = (s: Sale) => {
    const sid = s.stock_item_id ?? null;
    let purchase_price: number;
    if (sid != null) {
      const it = stockItems.find(i => i.id === sid);
      if (it) purchase_price = stockTotalAcquisition(it);
      else if (s.purchase_price != null) purchase_price = s.purchase_price;
      else purchase_price = 0;
    } else {
      purchase_price = s.purchase_price ?? 0;
    }
    return {
      stock_item_id: sid,
      purchase_price,
      crm_tva_rate: s.crm_tva_rate ?? tvaRate,
      crm_packaging_cost: s.crm_packaging_cost ?? packagingCost,
    };
  };

  const bulkMarkShipped = async () => {
    if (selected.size === 0) {
      showNotification('Aucune sélection', 'Coche une ou plusieurs ventes pour les marquer comme expédiées', 'warning');
      return;
    }
    const toShip = sales.filter(s => selected.has(s.id) && (s.status === 'sold' || s.status === 'packed'));
    if (toShip.length === 0) {
      showNotification('Aucune vente à expédier', 'Les ventes sélectionnées sont déjà expédiées ou finalisées', 'warning');
      return;
    }
    await Promise.all(toShip.map(s => handleStatusChange(s.id, 'shipped', purchaseMetaForShip(s))));
    setSelected(new Set());
    showNotification(`${toShip.length} vente(s) marquée(s) comme expédiée(s)`, '', 'success');
  };

  const bulkMarkCompleted = async () => {
    if (selected.size === 0) {
      showNotification('Aucune sélection', 'Coche une ou plusieurs ventes à finaliser', 'warning');
      return;
    }
    const eligible = sales.filter(s => selected.has(s.id) && (s.status === 'shipped' || s.status === 'delivered'));
    if (eligible.length === 0) {
      showNotification('Aucune vente éligible', 'Sélectionne des ventes en cours (expédié ou livré)', 'warning');
      return;
    }
    await Promise.all(eligible.map(s => handleStatusChange(s.id, 'completed')));
    setSelected(new Set());
    showNotification(`${eligible.length} vente(s) finalisée(s)`, '', 'success');
  };

  const handleToggleSalesSync = async () => {
    const next = !syncEnabled;
    setSyncEnabled(next);
    try {
      await apiClient.patch('/crm/settings', { crm_sales_sync_enabled: next });
    } catch {
      setSyncEnabled(!next);
      showNotification('Erreur', 'Impossible de sauvegarder la synchronisation', 'error');
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) {
      showNotification('Aucune sélection', 'Coche une ou plusieurs ventes pour les supprimer', 'warning');
      return;
    }
    if (!window.confirm(`Supprimer ${selected.size} vente(s) ?`)) return;
    await Promise.all(Array.from(selected).map(id => apiClient.delete(`/crm/sales/${id}`).catch(() => {})));
    setSales(prev => prev.filter(s => !selected.has(s.id)));
    setSelected(new Set());
    showNotification(`${selected.size} vente(s) supprimée(s)`, '', 'success');
  };

  // ── Conflits de bordereau ─────────────────────────────────────────────────

  const resolveLabelConflict = async (conflictId: number, body: { action?: string; candidate_sale_id?: number }) => {
    try {
      await apiClient.post(`/crm/sales/label-conflicts/${conflictId}/resolve`, body);
      await loadLabelConflicts();
      await loadSales();
      if (onConflictCountChange) onConflictCountChange();
      showNotification('Conflit résolu', '', 'success');
    } catch {
      showNotification('Erreur', 'Impossible de résoudre le conflit', 'error');
    }
  };

  const dismissLabelConflict = async (conflictId: number) => {
    try {
      await apiClient.delete(`/crm/sales/label-conflicts/${conflictId}`);
      setLabelConflicts(prev => prev.filter(c => c.id !== conflictId));
      if (onConflictCountChange) onConflictCountChange();
    } catch {
      showNotification('Erreur', 'Impossible d\'ignorer le conflit', 'error');
    }
  };

  // ── Filtrage ──────────────────────────────────────────────────────────────

  const tabDef = TABS.find(t => t.key === activeTab)!;
  const filtered = tabDef.statuses
    ? sales.filter(s => tabDef.statuses!.includes(s.status))
    : sales;

  const tabCount = (tab: typeof TABS[0]) =>
    tab.statuses ? sales.filter(s => tab.statuses!.includes(s.status)).length : sales.length;

  const urgentCount = sales.filter(s =>
    isShippingUrgent(s.shipping_deadline) && (s.status === 'sold' || s.status === 'packed')
  ).length;

  return (
    <div className="flex flex-col h-full min-h-0 bg-gray-50">
      <div className="flex-shrink-0 bg-gray-50 px-6 pt-6 pb-3 border-b border-gray-100">
      {/* En-tête : titre à gauche, actions à droite (même rangée) */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Tag size={24} className="text-emerald-600" /> Ventes
          </h1>
          <p className="text-gray-500 text-sm mt-0.5 flex items-center">
            {sales.length} vente{sales.length > 1 ? 's' : ''}
            {is_free_plan && (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ml-2 ${sales.length >= 10 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700'}`}>
                {Math.min(sales.length, 10)} / 10
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-2 w-full sm:w-auto min-w-0 max-w-xl">
          {!crm_email_active && <CrmEmailSyncBanner />}
          <div className="flex flex-wrap gap-2 items-center justify-end">
          {/* Synchroniser — pleine largeur sur mobile */}
          <div className="relative flex items-center gap-2 rounded-full border border-orange-200/80 bg-orange-50/70 px-3 py-2 shadow-sm w-full sm:w-auto">
            <button
              type="button"
              onClick={handleToggleSalesSync}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-bold transition-all duration-300 flex-1 ${
                syncEnabled
                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-200/80 ring-2 ring-orange-300'
                  : 'bg-orange-100/90 text-orange-600 hover:bg-orange-200/90'
              }`}
              title="Synchroniser emails vendus & bordereaux (par nom d'article)"
            >
              <div
                className={`relative w-14 h-7 rounded-full flex-shrink-0 overflow-hidden transition-colors duration-300 ${
                  syncEnabled ? 'bg-orange-600' : 'bg-orange-200'
                }`}
              >
                <span
                  className={`absolute inset-0 flex items-center text-[9px] font-bold pointer-events-none ${
                    syncEnabled ? 'justify-start pl-2 text-white' : 'justify-end pr-2 text-orange-700/80'
                  }`}
                >
                  {syncEnabled ? 'ON' : 'OFF'}
                </span>
                <span
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all duration-300 ${
                    syncEnabled ? 'left-8' : 'left-1'
                  }`}
                />
              </div>
              Synchroniser
            </button>
            <button
              type="button"
              onClick={() => setShowSyncInfo(v => !v)}
              className="w-10 h-10 rounded-full bg-orange-100/90 text-orange-500 hover:bg-orange-200 hover:text-orange-600 text-sm font-bold flex items-center justify-center transition-colors flex-shrink-0"
              title="En savoir plus sur la synchronisation"
            >
              ?
            </button>
            {showSyncInfo && (
              <div className="absolute right-0 top-9 z-50 w-80 bg-white border border-gray-200 rounded-2xl shadow-xl p-4">
                <div className="flex items-start gap-2 mb-2">
                  <span className="text-orange-400 text-lg">💡</span>
                  <p className="text-xs font-semibold text-gray-800">Synchronisation ventes & bordereaux</p>
                  <button onClick={() => setShowSyncInfo(false)} className="ml-auto text-gray-400 hover:text-gray-600"><X size={14} /></button>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Quand activé, le système associe automatiquement le <strong>prix de vente</strong> et le <strong>nom de l'acheteur</strong>{' '}
                  (email "Ton article s'est vendu") à chaque bordereau reçu, via le <strong>nom de l'article</strong>.
                </p>
                <div className="mt-3 bg-amber-50 rounded-xl p-3 border border-amber-100">
                  <p className="text-xs font-semibold text-amber-800 mb-1">⚠️ Important</p>
                  <p className="text-xs text-amber-700">
                    Donne un <strong>nom unique à chaque article</strong> posté sur Vinted.
                    Ex : "PS5 Slim blanche 1To" plutôt que "PS5".
                  </p>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowSettingsModal(true)}
            title="TVA & frais d'emballage"
            className="p-2 rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 transition-all">
            <Settings2 size={16} />
          </button>
          <button onClick={loadSales} className="p-2 rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 transition-all">
            <RefreshCw size={16} />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-semibold shadow hover:opacity-90">
            <Plus size={16} /> Ajouter
          </button>
          </div>{/* fin flex gap-2 actions */}
        </div>{/* fin flex-col droite */}
      </div>{/* fin flex justify-between titre+actions */}

      {/* Alerte expéditions urgentes */}
      {urgentCount > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-3 py-2 mb-3 flex items-center gap-2">
          <span className="text-base">⚡</span>
          <p className="text-xs font-semibold text-amber-800">
            {urgentCount} expédition{urgentCount > 1 ? 's' : ''} urgente{urgentCount > 1 ? 's' : ''} dans les 48h
          </p>
          <button onClick={() => setActiveTab('to_ship')} className="ml-auto text-xs text-amber-700 underline font-medium">
            Voir
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1.5 mb-3">
        <div className="flex gap-1.5 flex-wrap flex-1">
          {TABS.map(tab => {
            const count = tabCount(tab);
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`text-sm px-4 py-2 rounded-xl font-medium transition-all ${
                  isActive
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                {tab.label}
                {count > 0 && (
                  <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-white/20 text-white' : 'bg-white text-gray-700'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Info TVA active */}
      {(tvaRate > 0 || packagingCost > 0) && (
        <div className="bg-white border border-gray-200 rounded-xl px-3 py-2 mb-3 flex items-center gap-2 text-xs text-gray-500">
          <Settings2 size={12} />
          <span>
            Revenu net = prix − TVA {tvaRate}%{packagingCost > 0 ? ` − emballage ${packagingCost.toFixed(2)} €` : ''}
          </span>
          <button onClick={() => setShowSettingsModal(true)} className="ml-auto text-emerald-600 underline font-medium">
            Modifier
          </button>
        </div>
      )}

      {/* Barre d'actions groupées */}
      {filtered.length > 0 && (
        <div className="flex items-center gap-3 px-1">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={selected.size === filtered.length && filtered.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 rounded border-gray-300 accent-emerald-600 cursor-pointer"
            />
            <span className="text-xs text-gray-500 font-medium">
              {selected.size > 0 ? `${selected.size} sélectionné${selected.size > 1 ? 's' : ''}` : 'Tout sélectionner'}
            </span>
          </label>

          <div className="flex gap-2 ml-auto flex-wrap justify-end">
            {(activeTab === 'to_ship' || activeTab === 'all') && (
              <button
                type="button"
                onClick={bulkMarkShipped}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold hover:bg-blue-100">
                <Truck size={13} /> Expédié
              </button>
            )}
            {activeTab === 'in_progress' && (
              <button
                type="button"
                onClick={bulkMarkCompleted}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold hover:bg-indigo-100">
                <CheckCircle2 size={13} /> Marquer comme livré
              </button>
            )}
            {(activeTab === 'to_ship' || activeTab === 'all') && (
              <button
                type="button"
                onClick={bulkOpenLabels}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-100">
                <ExternalLink size={13} /> Voir bordereaux
              </button>
            )}
            {activeTab === 'finalized' && (
              <button
                type="button"
                onClick={() => setShowInvoiceModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition-colors">
                <FileText size={13} /> Factures
              </button>
            )}
            <button
              type="button"
              onClick={bulkDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-100">
              <Trash2 size={13} /> Supprimer
            </button>
          </div>
        </div>
      )}
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 px-6 pb-6 pt-3">
      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw size={24} className="animate-spin text-emerald-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Tag size={48} className="mx-auto mb-3 opacity-30" />
          <p className="text-base font-medium">Aucune vente dans cette catégorie</p>
          {activeTab === 'all' && (
            <p className="text-sm mt-1">Les ventes Vinted apparaissent automatiquement dès réception des emails</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {is_free_plan && filtered.length > 10 && (
            <div className="relative mb-2">
              <div className="blur-sm pointer-events-none select-none space-y-2">
                {filtered.slice(0, filtered.length - 10).map(s => (
                  <SaleCard key={s.id} sale={s} listTab={activeTab} defaultTvaRate={tvaRate} defaultPackagingCost={packagingCost} stockItems={stockItems} selected={false} syncEnabled={syncEnabled} showNotification={showNotification} onToggleSelect={() => {}} onStatusChange={() => {}} onPatchPurchaseLink={async () => {}} onPatchSalePrice={async () => {}} onPatchCrmFiscal={async () => {}} />
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 text-center shadow-xl border border-gray-100 mx-4 max-w-xs">
                  <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                    <Lock size={22} className="text-emerald-500" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-1">Limite gratuite atteinte</h4>
                  <p className="text-xs text-gray-500 mb-4">Passez à Starter pour un CRM illimité.</p>
                  <button onClick={onUpgrade} className="px-5 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm font-semibold hover:opacity-90 transition-all shadow">
                    Voir les plans →
                  </button>
                </div>
              </div>
            </div>
          )}
          {(is_free_plan && filtered.length > 10 ? filtered.slice(filtered.length - 10) : filtered).map(s => (
            <SaleCard
              key={s.id}
              sale={s}
              listTab={activeTab}
              defaultTvaRate={tvaRate}
              defaultPackagingCost={packagingCost}
              stockItems={stockItems}
              selected={selected.has(s.id)}
              syncEnabled={syncEnabled}
              showNotification={showNotification}
              onToggleSelect={toggleSelect}
              onStatusChange={handleStatusChange}
              onPatchPurchaseLink={patchSalePurchaseLink}
              onPatchSalePrice={patchSalePrice}
              onPatchCrmFiscal={patchSaleCrmFiscal}
            />
          ))}
        </div>
      )}
      </div>

      {showBulkLabelModal && (
        <BulkLabelModal
          count={sales.filter(s => selected.has(s.id) && s.shipping_label_filename).length}
          onClose={() => {
            if (mergedBlobUrl) URL.revokeObjectURL(mergedBlobUrl.url);
            setShowBulkLabelModal(false);
            setLabelMergeProgress(null);
            setMergedBlobUrl(null);
          }}
          onChoose={handleBulkLabelChoose}
          progress={labelMergeProgress}
          readyUrl={mergedBlobUrl}
        />
      )}
      {showAddModal && (
        <AddSaleModal
          onClose={() => setShowAddModal(false)}
          onSaved={() => {
            loadSales();
            apiClient.get('/crm/stock?scope=effective').then(r => setStockItems(r.data)).catch(() => {});
          }}
          initialStatus={
            activeTab === 'in_progress' ? 'shipped'
            : activeTab === 'finalized' ? 'completed'
            : 'sold'
          }
        />
      )}
      {showSettingsModal && (
        <SettingsModal
          tvaRate={tvaRate}
          packagingCost={packagingCost}
          vatNumber={vatNumber}
          onSave={handleSaveSettings}
          onClose={() => setShowSettingsModal(false)}
        />
      )}
      {showInvoiceModal && (
        <SalesInvoiceModal
          sales={sales}
          preSelected={selected}
          onClose={() => setShowInvoiceModal(false)}
          showNotification={showNotification}
        />
      )}

      {/* ── Popup conflits de bordereau ───────────────────────────────────── */}
      {labelConflicts.length > 0 && (() => {
        const conflict = labelConflicts[0];

        // Détection Case 3 : plusieurs conflits choose_sold_email avec le même titre
        const sameTitle = labelConflicts.filter(
          c => c.conflict_type === 'choose_sold_email' && c.item_title === conflict.item_title
        );
        const isCase3 = sameTitle.length >= 2 && conflict.conflict_type === 'choose_sold_email';

        // ── Case 1 : remplacer le bordereau ──────────────────────────────────
        if (conflict.conflict_type === 'replace_label') {
          const existing = conflict.candidates[0];
          return (
            <div className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className="text-amber-500 text-lg">⚠️</span>
                    <h3 className="text-base font-bold text-gray-900">Bordereau en doublon</h3>
                  </div>
                  <button onClick={() => dismissLabelConflict(conflict.id)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                </div>
                <div className="p-6 space-y-4">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Un nouveau bordereau a été reçu pour <strong className="text-gray-900">{conflict.item_title}</strong>, mais un bordereau est déjà associé à cet article. Souhaitez-vous lui associer ce nouveau bordereau à la place ?
                  </p>
                  {existing && (
                    <div className="bg-gray-50 rounded-xl px-4 py-3 space-y-1">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Bordereau actuel</p>
                      {existing.tracking_number && <p className="text-xs text-gray-600">Suivi : {existing.tracking_number}</p>}
                      {existing.buyer_name && <p className="text-xs text-gray-600">Acheteur : {existing.buyer_name}</p>}
                      {existing.sale_price != null && existing.sale_price > 0 && (
                        <p className="text-xs font-semibold text-emerald-700">{formatPrice(existing.sale_price)}</p>
                      )}
                    </div>
                  )}
                  <div className="flex gap-3 pt-1">
                    <button
                      onClick={() => dismissLabelConflict(conflict.id)}
                      className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50">
                      Garder l'ancien
                    </button>
                    <button
                      onClick={() => resolveLabelConflict(conflict.id, { action: 'replace' })}
                      className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-semibold hover:opacity-90">
                      Remplacer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        }

        // ── Case 3 : plusieurs bordereaux + plusieurs ventes (même titre) ────
        if (isCase3) {
          const allBordereaux = sameTitle
            .map(c => c.trigger_sale)
            .filter(s => !s.from_sold_email);
          const allValider = sameTitle[0].candidates.filter(s => s.from_sold_email);
          return (
            <div className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
                <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className="text-amber-500 text-lg">⚠️</span>
                    <div>
                      <h3 className="text-base font-bold text-gray-900">Conflits de bordereaux</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{conflict.item_title}</p>
                    </div>
                  </div>
                  <button onClick={() => dismissLabelConflict(conflict.id)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                </div>
                <div className="p-6 space-y-4">
                  <div className="bg-amber-50 rounded-xl px-4 py-3 border border-amber-100">
                    <p className="text-xs text-amber-800 leading-relaxed">
                      À quelles ventes correspondent ces bordereaux ? Sélectionne la vente correspondant à chaque bordereau.
                    </p>
                  </div>
                  {allBordereaux.map(bordereau => (
                    <div key={bordereau.id} className="space-y-1.5">
                      <div className="bg-gray-50 rounded-xl px-3 py-2">
                        <p className="text-xs font-semibold text-gray-600">Bordereau</p>
                        {bordereau.tracking_number && <p className="text-xs text-gray-500">Suivi : {bordereau.tracking_number}</p>}
                        {bordereau.carrier && <p className="text-xs text-gray-400">{bordereau.carrier}</p>}
                      </div>
                      <div className="pl-2 space-y-1">
                        {allValider.map(av => {
                          const conflictForBordereau = sameTitle.find(c => c.trigger_sale.id === bordereau.id);
                          return (
                            <button
                              key={av.id}
                              onClick={() => conflictForBordereau && resolveLabelConflict(conflictForBordereau.id, { candidate_sale_id: av.id })}
                              className="w-full text-left flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all group text-xs"
                            >
                              <span className="font-medium text-gray-800 group-hover:text-emerald-700">{av.buyer_name || '—'}</span>
                              {av.sale_price != null && av.sale_price > 0 && (
                                <span className="font-bold text-emerald-700 ml-2">{formatPrice(av.sale_price)}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => dismissLabelConflict(conflict.id)}
                    className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-500 text-sm font-medium hover:bg-gray-50">
                    Ignorer
                  </button>
                </div>
              </div>
            </div>
          );
        }

        // ── Case 2 : choisir à quelle vente correspond ce bordereau ──────────
        return (
          <div className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <span className="text-amber-500 text-lg">⚠️</span>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">
                      {conflict.trigger_sale.from_sold_email
                        ? 'À quel bordereau correspond cette vente ?'
                        : 'À quelle vente correspond ce bordereau ?'}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {labelConflicts.length > 1 ? `${labelConflicts.length} conflits en attente` : '1 conflit en attente'}
                    </p>
                  </div>
                </div>
                <button onClick={() => dismissLabelConflict(conflict.id)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
              </div>
              <div className="p-6 space-y-4">
                {/* Info du trigger */}
                <div className="bg-gray-50 rounded-xl px-4 py-3 space-y-1">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    {conflict.trigger_sale.from_sold_email ? 'Email vendu reçu' : 'Bordereau reçu'}
                  </p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{conflict.trigger_sale.item_title}</p>
                  {conflict.trigger_sale.from_sold_email && conflict.trigger_sale.buyer_name && (
                    <p className="text-xs text-gray-500">Acheteur : {conflict.trigger_sale.buyer_name}</p>
                  )}
                  {conflict.trigger_sale.from_sold_email && (conflict.trigger_sale.sale_price ?? 0) > 0 && (
                    <p className="text-xs font-semibold text-emerald-700">{formatPrice(conflict.trigger_sale.sale_price)}</p>
                  )}
                  {!conflict.trigger_sale.from_sold_email && conflict.trigger_sale.tracking_number && (
                    <p className="text-xs text-gray-500">Suivi : {conflict.trigger_sale.tracking_number}</p>
                  )}
                  {!conflict.trigger_sale.from_sold_email && conflict.trigger_sale.carrier && (
                    <p className="text-xs text-gray-400">{conflict.trigger_sale.carrier}</p>
                  )}
                </div>

                {/* Candidats */}
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-700">Choisir la correspondance :</p>
                  {conflict.candidates.map(candidate => (
                    <button
                      key={candidate.id}
                      onClick={() => resolveLabelConflict(conflict.id, { candidate_sale_id: candidate.id })}
                      className="w-full text-left flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 bg-white hover:border-emerald-400 hover:bg-emerald-50 transition-all group"
                    >
                      <div className="min-w-0">
                        {candidate.from_sold_email ? (
                          <>
                            <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-emerald-700">
                              {candidate.buyer_name || '—'}
                            </p>
                            {candidate.created_at && (
                              <p className="text-xs text-gray-400 mt-0.5">{formatDate(candidate.created_at)}</p>
                            )}
                          </>
                        ) : (
                          <>
                            <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-emerald-700">
                              {candidate.tracking_number || candidate.item_title}
                            </p>
                            {candidate.carrier && (
                              <p className="text-xs text-gray-400">{candidate.carrier}</p>
                            )}
                          </>
                        )}
                      </div>
                      <div className="ml-3 flex-shrink-0 text-right">
                        {(candidate.sale_price ?? 0) > 0 && (
                          <span className="text-sm font-bold text-emerald-700">{formatPrice(candidate.sale_price)}</span>
                        )}
                        <Check size={14} className="ml-2 text-gray-300 group-hover:text-emerald-500 inline-block" />
                      </div>
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => dismissLabelConflict(conflict.id)}
                  className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-500 text-sm font-medium hover:bg-gray-50">
                  Ignorer ce conflit
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CrmVentes;
